/**
 * Monitoring View
 * 
 * Real-time monitoring dashboard.
 */

import React, { useState, useEffect } from 'react';
import { Zap, Play, Square, Globe, AlertTriangle } from 'lucide-react';

export default function Monitoring({ apiUrl }) {
  const [targets, setTargets] = useState(['Taiwan', 'Ukraine', 'South China Sea']);
  const [monitoring, setMonitoring] = useState(false);
  const [feed, setFeed] = useState(null);
  const [newTarget, setNewTarget] = useState('');

  useEffect(() => {
    if (monitoring) {
      const interval = setInterval(loadFeed, 30000);
      loadFeed();
      return () => clearInterval(interval);
    }
  }, [monitoring]);

  async function loadFeed() {
    try {
      const response = await fetch(`${apiUrl}/intelligence/feed`);
      const data = await response.json();
      setFeed(data);
    } catch (err) {
      console.error(err);
    }
  }

  async function toggleMonitoring() {
    if (monitoring) {
      await fetch(`${apiUrl}/monitoring/stop`, { method: 'POST' });
      setMonitoring(false);
    } else {
      await fetch(`${apiUrl}/monitoring/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targets, interval: 60000 })
      });
      setMonitoring(true);
    }
  }

  function addTarget() {
    if (newTarget && !targets.includes(newTarget)) {
      setTargets([...targets, newTarget]);
      setNewTarget('');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Real-Time Monitoring</h1>
        <p className="text-slate-400">Continuous crisis monitoring and alerts</p>
      </div>

      {/* Controls */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Monitor Targets</h2>
          <button
            onClick={toggleMonitoring}
            className={`px-6 py-3 rounded-lg font-medium flex items-center ${
              monitoring ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'
            }`}
          >
            {monitoring ? (
              <>
                <Square className="w-5 h-5 mr-2" />
                Stop Monitoring
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start Monitoring
              </>
            )}
          </button>
        </div>

        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
            placeholder="Add target..."
            className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            onKeyDown={(e) => e.key === 'Enter' && addTarget()}
          />
          <button onClick={addTarget} className="px-4 py-2 bg-cyan-600 rounded-lg">Add</button>
        </div>

        <div className="flex flex-wrap gap-2">
          {targets.map((target, i) => (
            <span key={i} className="px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg flex items-center">
              <Globe className="w-4 h-4 mr-2" />
              {target}
              {monitoring && <Zap className="w-4 h-4 ml-2 animate-pulse text-yellow-400" />}
            </span>
          ))}
        </div>
      </div>

      {/* Live Feed */}
      {monitoring && (
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-yellow-400 animate-pulse" />
            Live Feed
          </h2>
          
          {feed?.warnings?.length > 0 && (
            <div className="space-y-2">
              {feed.warnings.map((warning, i) => (
                <div key={i} className="p-3 bg-yellow-500/20 border border-yellow-500 rounded-lg flex items-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3" />
                  <span>{warning.message || JSON.stringify(warning)}</span>
                </div>
              ))}
            </div>
          )}
          
          {(!feed?.warnings || feed.warnings.length === 0) && (
            <div className="text-center text-slate-400 py-8">
              No alerts - monitoring active
            </div>
          )}
        </div>
      )}
    </div>
  );
}
